import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import QuickSearch from '../components/SearchResults/QuickSearch';
import ActiveFilters from '../components/SearchResults/ActiveFilters';
import FilterSidebar from '../components/SearchResults/FilterSidebar';
import CategorySelector from '../components/SearchResults/CategorySelector';
import SearchResultsList from '../components/SearchResults/SearchResultsList';
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import SEO from '../components/SEO/SEO';
import JeepHero from '../assets/images/JEEPSUV.jpg';

const SearchResults = () => {
const location = useLocation();
const { 
  selectedCategory = [],
  pickUpDate,
  dropOffDate,
  pickUpLocation = '',
  dropOffLocation = '',
  customPickupLocation = '',
  customDropoffLocation = '',
  originalPickupLocation = '',
  originalDropoffLocation = '',
  pickUpTime = '',
  dropOffTime = '',
  age = '26+',
  couponCode = '',
  dynamicPriceMap = {} // Get dynamic price data if passed from the form
} = location.state || {};

  const [filters, setFilters] = useState({
    category: selectedCategory || [],
    dateRange: { 
      pickup: pickUpDate?.toISOString() || '', 
      dropoff: dropOffDate?.toISOString() || '',
      pickupTime: pickUpTime || (pickUpDate ? `${pickUpDate.getHours().toString().padStart(2, '0')}:${pickUpDate.getMinutes().toString().padStart(2, '0')}` : ''),
      dropoffTime: dropOffTime || (dropOffDate ? `${dropOffDate.getHours().toString().padStart(2, '0')}:${dropOffDate.getMinutes().toString().padStart(2, '0')}` : '')
    },
    priceRange: [20, 390],
    passengers: '',
    transmission: '',
    pickupLocation: originalPickupLocation || '',
    dropoffLocation: originalDropoffLocation || '',
    customPickupLocation: customPickupLocation || '',
    customDropoffLocation: customDropoffLocation || '',
    age: age || '26+',
    couponCode: couponCode || '',
    dynamicPriceMap: dynamicPriceMap || {} // Initialize with dynamic prices from form
  });

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      category: selectedCategory || []
    }));
  }, [location.state]); 

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handleRemoveFilter = (filterType, value) => {
    switch(filterType) {
      case 'dateRange':
        setFilters(prev => ({ ...prev, dateRange: { pickup: '', dropoff: '', pickupTime: '', dropoffTime: '' } }));
        break;
      case 'category':
        setFilters(prev => ({
          ...prev,
          category: prev.category.filter(cat => cat !== value)
        }));
        break;
      case 'passengers':
        setFilters(prev => ({ ...prev, passengers: '' }));
        break;
      case 'transmission':
        setFilters(prev => ({ ...prev, transmission: '' }));
        break;
      case 'priceRange':
        setFilters(prev => ({ ...prev, priceRange: [20, 390] }));
        break;
      default:
        break;
    }
  };

  const handleRemoveAllFilters = () => {
    setFilters({
      category: [],
      dateRange: { pickup: '', dropoff: '', pickupTime: '', dropoffTime: '' },
      priceRange: [20, 390],
      passengers: '',
      transmission: '',
      pickupLocation: '',
      dropoffLocation: '',
      customPickupLocation: '',
      customDropoffLocation: '',
      age: '26+',
      couponCode: ''
    });
  };

  const handleCategoryChange = (newCategories) => {
    setFilters(prev => ({
      ...prev,
      category: newCategories
    }));
  };
  const handleQuickSearchChange = (searchData) => {
    // Extract dates and dynamic price map
    const { pickup, dropoff, dynamicPriceMap } = searchData;
    
    setFilters(prev => ({
      ...prev,
      dateRange: {
        pickup,
        dropoff
      },
      dynamicPriceMap: dynamicPriceMap || {} // Store dynamic price data if available
    }));
  };

  const isJeepWrangler = filters.category.includes('Jeep Wrangler');
  // Prepare data for SEO
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://mykonosrentacars.com';
  const currentUrl = `${origin}/search`;
  const title = "Search Results • Luxury Car Mykonos";
  const description = "Browse available rentals matching your dates, locations and preferences on Luxury Car Mykonos.";
  
  return (
<>
      <SEO
        title={title}
        description={description}
        canonicalUrl={currentUrl}
        imageUrl={`${origin}/og-search.jpg`}
        imageAlt="Luxury Car Mykonos - Search Results"
        structuredData={{
          "@context": "https://schema.org/",
          "@type": "WebPage",
          "name": title,
          "description": description,
          "url": currentUrl,
          "provider": {
            "@type": "Organization",
            "name": "Luxury Car Mykonos"
          }
        }}
      />
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main>
        {isJeepWrangler && (
          <section className="relative h-[600px] overflow-hidden mb-8">
            <div className="absolute inset-0">
              <img
                src={JeepHero}
                alt="Jeep Wrangler"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black opacity-50" />
            </div>
            <div className="relative h-full container mx-auto px-4 flex flex-col justify-center items-center text-center text-white">
              <motion.h1 
                className="text-4xl md:text-6xl font-bold mb-6 max-w-4xl Aleo"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Unleash the Greek Adventure with Jeep Wrangler Mykonos
              </motion.h1>
              <motion.p 
                className="text-lg md:text-xl mb-8 max-w-3xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Experience the thrill of off-roading in paradise with Jeep Wrangler Mykonos. 
                Embrace the essence of Greek island life as you navigate rugged terrains and 
                soak in breathtaking coastal views. Explore the unbeaten paths and discover 
                the hidden gems of Mykonos with the iconic Jeep Wrangler, your ultimate 
                companion for adventure and luxury. Get ready to make unforgettable memories 
                on the roads less traveled!
              </motion.p>
            </div>
          </section>
        )}

        <div className="container mx-auto px-4 py-8">
          <button
            className="lg:hidden w-full bg-white p-4 mb-4 rounded-lg shadow flex items-center justify-between"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            <span className="font-semibold">Filters</span>
            {isFilterOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </button>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Sidebar */}
            <div className={`lg:w-1/3 ${isFilterOpen ? 'block' : 'hidden lg:block'}`}>
              <div className="space-y-6">
                <QuickSearch
                  initialDates={filters.dateRange}
                  onSearch={handleQuickSearchChange}
                />
                <ActiveFilters
                  filters={filters}
                  onRemoveFilter={handleRemoveFilter}
                  onRemoveAll={handleRemoveAllFilters}
                />
                <FilterSidebar
                  filters={filters}
                  setFilters={setFilters}
                />
              </div>
            </div>
            
            {/* Main Content */}
            <div className="lg:w-2/3">
              {!isJeepWrangler && (
                <CategorySelector
                  selectedCategory={filters.category}
                  onCategoryChange={handleCategoryChange}
                />
              )}
              <SearchResultsList filters={filters} />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
</>
  );
};

export default SearchResults;